import { observable, action } from 'mobx';
import {
    IProduct,
    IVariant,
    IRatingsReviewsConfig,
    IComponents,
    IPRProductProduct
} from '../../interfaces/IRating';
import {
    REVIEW_DISPLAY,
    QUESTION_DISPLAY,
    SNAPSHOT_TYPE,
    PAGINATION_TYPE
} from '../../constants/PowerReviews';
import { emit, events } from 'elc-service-bus';
import { REVIEW_CATEGORY } from '../../constants/ServiceReviews';
import { InventoryStatuses } from '../../constants/InventoryStatuses';

export class PowerReviewsProduct {
    @observable public reviewDisplayId: string;
    @observable private isShaded: boolean;
    @observable private skuBaseId: number;
    @observable public api_key: string;
    @observable public locale: string;
    @observable public merchant_group_id: string;
    @observable public merchant_id: string;
    @observable public page_id: string;
    @observable public review_wrapper_url: string;
    @observable public components: IComponents = {
        ReviewDisplay: REVIEW_DISPLAY,
        QuestionDisplay: QUESTION_DISPLAY
    };
    @observable public REVIEW_DISPLAY_SNAPSHOT_TYPE: string;
    @observable public REVIEW_DISPLAY_PAGINATION_TYPE = PAGINATION_TYPE.DEFAULT;
    @observable public variants: IVariant[];
    @observable public product: IPRProductProduct;
    public on_submit: () => void;

    public static fromJson(
        data: IProduct,
        ratingsReviewsConfig: IRatingsReviewsConfig,
        paginationType: string,
        selectedSkuId?: string
    ): PowerReviewsProduct {
        const prProduct = new PowerReviewsProduct();

        const {
            apiKey,
            locale,
            merchantGroupId,
            merchantId,
            pageId,
            reviewWrapperUrl,
            reviewDisplaySnapshotType
        } = ratingsReviewsConfig;

        const productBaseId = data.product_id;
        const skuBaseId = data.skus[0].perlgem.SKU_BASE_ID;
        const baseUrl = `${window.location.protocol}//${document.location.hostname}`;
        let inStock = !data.is_out_of_stock;

        prProduct.reviewDisplayId = `${productBaseId}-${skuBaseId.toString()}-1-${new Date()
            .getTime()
            .toString()}`;
        prProduct.skuBaseId = skuBaseId;
        prProduct.isShaded = data.shades && !!data.shades.length;

        if (selectedSkuId) {
            const selectedSku = data.skus.find((sku) => sku.skuId === selectedSkuId);
            if (selectedSku) {
                inStock = selectedSku.inventoryStatus === InventoryStatuses.Active;
            }
        }
        prProduct.api_key = apiKey;
        prProduct.locale = locale;
        prProduct.merchant_group_id = merchantGroupId;
        prProduct.merchant_id = merchantId;
        prProduct.page_id = pageId || productBaseId;
        prProduct.REVIEW_DISPLAY_SNAPSHOT_TYPE = reviewDisplaySnapshotType || SNAPSHOT_TYPE;
        prProduct.review_wrapper_url = prProduct.reviewUrl(reviewWrapperUrl);
        prProduct.product = {
            name: data.display_name,
            url: baseUrl.concat(data.product_url),
            image_url: baseUrl.concat(prProduct.reviewsImage(data)),
            description: data.short_description,
            category_name: data.default_category.id,
            upc: data.upc_code,
            brand_name: data.brand,
            price: data.price,
            in_stock: inStock
        };
        prProduct.variants = prProduct.isShaded ? prProduct.productVariants(data) : [];

        if (paginationType === PAGINATION_TYPE.VERTICAL) {
            prProduct.REVIEW_DISPLAY_PAGINATION_TYPE = paginationType;
        }

        prProduct.on_submit = function () {
            prProduct.onPrFormSubmit(data);
        };

        return prProduct;
    }

    private onPrFormSubmit(data: IProduct) {
        const payLoad = {
            id: data.productId,
            name: data.displayName,
            reviewCategory: REVIEW_CATEGORY
        };
        emit(events.ASK_AND_ANSWER_SUBMITTED, { ...payLoad });
    }

    @action
    private reviewUrl(reviewWrapperUrl: string): string {
        const url = reviewWrapperUrl || '/review/create';
        let queryString = `?pr_page_id=${this.page_id}`;

        if (this.isShaded) {
            queryString += `&pr_skuId=SKU${this.skuBaseId.toString()}`;
        }

        return `${url}${queryString}`;
    }

    @action
    private reviewsImage(data: IProduct): string {
        const hasMedia = !!data && !!data.skus && !!data.skus.length && !!data.skus[0].media;

        if (hasMedia) {
            const media = data.skus[0].media;
            if (!!media.large[0]) {
                return media.large[0].src;
            } else if (!!media.medium[0]) {
                return media.medium[0].src;
            }
        }

        return '';
    }

    public productVariants(data: IProduct) {
        return data.skus.reduce((variantAcc: IVariant[], sku) => {
            if (sku.shades.length) {
                variantAcc.push({
                    name: sku.shades[0].name,
                    description: sku.shades[0].description,
                    upc: sku.upcCode,
                    page_id_variant: sku.perlgem.SKU_BASE_ID
                });
            }

            return variantAcc;
        }, []);
    }
}
